import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Clock, Lock, X } from "lucide-react";
import { useLicensing } from "../../auth/LicensingProvider";
import { useIsOrgOrSuperAdmin } from "../../auth/permissions";
import { SubscriptionStatus } from "../../types/licensing.types";
import { daysUntil } from "../../utils/licensing";
import { licensingApi } from "../../api/licensingApi";
import { toast } from "./toast";

/**
 * One line above the page content: the subscription is blocked, the trial
 * is ending, or a meter is at 90%+. Silent otherwise. Dismissable per
 * session, per message, so the same warning does not nag on every page.
 */
export function LicensingBanner() {
  const { t } = useTranslation("licensing");
  const { snapshot } = useLicensing();
  const isAdmin = useIsOrgOrSuperAdmin();
  const navigate = useNavigate();
  const [requestState, setRequestState] = useState<"idle" | "sending" | "sent">("idle");
  const [dismissed, setDismissed] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("licensing:banner:dismissed");
    } catch {
      return null;
    }
  });

  const banner = useMemo(() => {
    if (!snapshot || snapshot.isUnrestricted) return null;

    if (snapshot.isBlocked) {
      return {
        key: `blocked:${snapshot.blockedReason}`,
        tone: "danger" as const,
        Icon: Lock,
        text: t("banner.blocked", {
          defaultValue: "Your subscription is {{reason}}. New contracts, uploads, signatures and AI are paused until it is renewed.",
          reason: (snapshot.blockedReason || "inactive").toLowerCase(),
        }),
      };
    }

    const hot = (snapshot.usage || []).filter((u) => (u.highestAlertThreshold || 0) >= 90 && !u.isUnlimited);
    if (hot.length > 0) {
      const worst = hot.sort((a, b) => (b.percentUsed || 0) - (a.percentUsed || 0))[0];
      const exhausted = (worst.percentUsed || 0) >= 100;
      return {
        key: `usage:${worst.key}:${worst.periodKey}:${worst.highestAlertThreshold}`,
        tone: exhausted ? ("danger" as const) : ("warn" as const),
        Icon: AlertTriangle,
        text: exhausted
          ? t("banner.exhausted", { defaultValue: "{{meter}} limit reached ({{used}} of {{included}} {{unit}}).", meter: worst.name, used: worst.used, included: worst.included, unit: worst.unit || "" })
          : t("banner.nearLimit", { defaultValue: "Only {{left}}% of your {{meter}} remain this period.", left: Math.max(0, 100 - Math.round(worst.percentUsed || 0)), meter: worst.name.toLowerCase() }),
      };
    }

    if (snapshot.status === SubscriptionStatus.Trial && snapshot.trialEndsAt) {
      const days = daysUntil(snapshot.trialEndsAt);
      if (days !== null && days <= 7) {
        return {
          key: `trial:${snapshot.trialEndsAt}`,
          tone: "info" as const,
          Icon: Clock,
          text:
            days <= 0
              ? t("banner.trialEndsToday", { defaultValue: "Your free trial ends today. Choose a plan to keep working without interruption." })
              : t("banner.trialEnding", { defaultValue: "Your free trial ends in {{days}} day(s). Choose a plan to keep working without interruption.", days }),
        };
      }
    }
    return null;
  }, [snapshot, t]);

  if (!banner || dismissed === banner.key) return null;

  const tone =
    banner.tone === "danger"
      ? "bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300"
      : banner.tone === "warn"
        ? "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300"
        : "bg-sky-500/10 border-sky-500/30 text-sky-700 dark:text-sky-300";

  return (
    <div className={`mx-3 sm:mx-4 md:mx-6 mt-3 flex items-center gap-3 rounded-xl border px-4 py-2.5 text-sm ${tone}`} role="status">
      <banner.Icon size={16} className="flex-shrink-0" />
      <span className="flex-1 min-w-0">{banner.text}</span>
      {isAdmin ? (
        <button
          type="button"
          onClick={() => navigate("/admin/subscription")}
          className="flex-shrink-0 rounded-lg px-3 py-1 text-xs font-semibold bg-[rgb(var(--accent-fill))] text-[rgb(var(--accent-ink))] hover:opacity-90"
        >
          {banner.tone === "info"
            ? t("banner.choosePlan", { defaultValue: "Choose a plan" })
            : t("banner.manage", { defaultValue: "Recharge / Upgrade" })}
        </button>
      ) : (
        // Anyone else asks the admins: one click, one email and one
        // notification to every Org Admin and Super Admin.
        <button
          type="button"
          disabled={requestState !== "idle"}
          onClick={async () => {
            setRequestState("sending");
            try {
              await licensingApi.requestUpgrade({});
              setRequestState("sent");
              toast.success(
                t("banner.requestSent", {
                  defaultValue: "Your organisation's admin has been asked to upgrade the subscription.",
                }),
              );
            } catch {
              setRequestState("idle");
              toast.error(
                t("banner.requestFailed", { defaultValue: "Could not send the request. Please try again." }),
              );
            }
          }}
          className="flex-shrink-0 rounded-lg px-3 py-1 text-xs font-semibold bg-[rgb(var(--accent-fill))] text-[rgb(var(--accent-ink))] hover:opacity-90 disabled:opacity-60"
        >
          {requestState === "sent"
            ? t("banner.requestSentShort", { defaultValue: "Request sent" })
            : t("banner.contactAdmin", { defaultValue: "Contact your org admin" })}
        </button>
      )}
      <button
        type="button"
        aria-label={t("banner.dismiss", { defaultValue: "Dismiss" })}
        onClick={() => {
          setDismissed(banner.key);
          try {
            sessionStorage.setItem("licensing:banner:dismissed", banner.key);
          } catch {
            /* ignore */
          }
        }}
        className="flex-shrink-0 opacity-70 hover:opacity-100"
      >
        <X size={14} />
      </button>
    </div>
  );
}
